import React from 'react';
import { Box, Typography } from '@mui/material';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../store';
import { MyDashboardTypes } from '../../../../types/my-dashboard.types';

const BLUE = '#206FA5';

interface AlertsZoneProps {
    onMetricClick: (key: MyDashboardTypes.MetricKey) => void;
}

export const AlertsZone: React.FC<AlertsZoneProps> = ({ onMetricClick }) => {
    const summary = useSelector((s: RootState) => s.myDashboard.summary);

    if (!summary) return null;

    // ?? 0 on each count: defensive against partial backend rollouts where
    // a field is missing/null. Without this, summary.X + undefined → NaN
    // silently corrupts the displayed totals.
    const sos = summary.sosAlerts ?? 0;
    const checkOnMe = summary.checkOnMe ?? 0;
    const fakeCalls = summary.fakeCalls ?? 0;
    const shifts = summary.shifts ?? 0;
    const totalAlerts = sos
        + (summary.checkOnMeEnabled ? checkOnMe : 0)
        + fakeCalls
        + (summary.shiftsEnabled ? shifts : 0);

    const rows: { label: string; key: MyDashboardTypes.MetricKey; count: number; urgent: boolean }[] = [
        { label: 'SOS', key: 'sosAlerts', count: sos, urgent: true },
        ...(summary.checkOnMeEnabled
            ? [{ label: 'Check On Me', key: 'checkOnMe' as MyDashboardTypes.MetricKey, count: checkOnMe, urgent: false }]
            : []),
        { label: 'Fake Calls', key: 'fakeCalls', count: fakeCalls, urgent: false },
        ...(summary.shiftsEnabled
            ? [{ label: 'Shifts', key: 'shifts' as MyDashboardTypes.MetricKey, count: shifts, urgent: false }]
            : []),
    ];

    return (
        <Box sx={zoneStyle}>
            {/* Header */}
            <ZoneHeader label="Alerts" color={BLUE} tag="7D" />

            {/* Total */}
            <Box sx={{ mb: 2.25 }}>
                <Typography sx={{ fontSize: 11, color: '#94A3B8', mb: 0.25, textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Total alerts
                </Typography>
                <Typography sx={{ fontSize: 40, fontWeight: 800, color: BLUE, letterSpacing: '-0.03em', lineHeight: 1 }}>
                    {totalAlerts}
                    <Typography component="span" sx={{ fontSize: 13, fontWeight: 500, color: '#94A3B8', ml: 1 }}>
                        last 7 days
                    </Typography>
                </Typography>
            </Box>

            {/* Alert breakdown */}
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25, mb: 2.5 }}>
                {rows.map(({ label, key, count, urgent }) => (
                    <Box
                        key={key}
                        onClick={() => onMetricClick(key)}
                        sx={{
                            display: 'flex', alignItems: 'center',
                            p: '10px 12px', borderRadius: '8px',
                            bgcolor: urgent && count > 0 ? `${BLUE}0E` : `${BLUE}06`,
                            border: `1px solid ${urgent && count > 0 ? BLUE + '30' : '#E8ECF0'}`,
                            cursor: 'pointer',
                            transition: 'all 0.15s',
                            '&:hover': { borderColor: BLUE + '60', bgcolor: `${BLUE}0F` },
                        }}
                    >
                        {urgent && count > 0 && (
                            <Box sx={{
                                mr: 1, width: 6, height: 6, borderRadius: '50%', bgcolor: BLUE,
                                animation: 'pulse 2s ease-in-out infinite',
                                '@keyframes pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.4 } },
                            }} />
                        )}
                        <Typography sx={{ flex: 1, fontSize: 12, fontWeight: urgent ? 600 : 400, color: '#1A2B3C' }}>
                            {label}
                        </Typography>
                        <Typography sx={{ fontSize: 20, fontWeight: 800, color: count > 0 ? BLUE : '#94A3B8' }}>
                            {count}
                        </Typography>
                    </Box>
                ))}
            </Box>

        </Box>
    );
};

function ZoneHeader({ label, color, tag }: { label: string; color: string; tag: string }) {
    return (
        <Box sx={{ mb: 2.25 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 3, height: 16, borderRadius: 1, bgcolor: color }} />
                    <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#64748B', letterSpacing: '0.1em', textTransform: 'uppercase' }}>
                        {label}
                    </Typography>
                </Box>
                <Box sx={{
                    fontSize: 10, fontWeight: 700, color,
                    bgcolor: `${color}12`, border: `1px solid ${color}30`,
                    px: 1, py: 0.25, borderRadius: 10,
                }}>
                    {tag}
                </Box>
            </Box>
        </Box>
    );
}

const zoneStyle = {
    flex: '1 1 280px',
    minWidth: 260,
    bgcolor: '#fff',
    border: '1px solid #E8ECF0',
    borderRadius: '14px',
    p: '22px 22px 16px',
    display: 'flex',
    flexDirection: 'column' as const,
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
};
